/**
 * iurop-cart-drawer.js — Sidecart dinámico (Glucody, base IUROP)
 *
 * - Mutaciones de carrito DENTRO del drawer (cantidad +/- y eliminar) vía
 *   /cart/change.js con Section Rendering → re-pinta el drawer en 1 ida.
 * - Adiciones EXTERNAS (PDP, cards, upsells) van por el quick-add global de
 *   header.js, que tras añadir dispara `cart:updated` → aquí refrescamos el
 *   drawer (Section Rendering por GET) y lo ABRIMOS.
 * - Temporizador de reserva: arranca al tener ítems, persiste en localStorage,
 *   se reinicia si vacías el carrito y lo empiezas de nuevo, y cambia el copy
 *   al llegar a 0. Minutos y textos son editables (settings → Liquid).
 * - Modal Klarna (mismo contenido que la PDP), scoped al drawer.
 *
 * El abrir/cerrar base lo gestiona global.js por atributos:
 *   [data-open] en [data-cart-drawer] + [data-drawer-open] en <html>.
 */
(function () {
  'use strict';

  var RESERVE_KEY = 'iurop:cart-reserve-start';
  var reserveTimer = null;

  function drawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  // --- Contadores de carrito (drawer + header) -----------------------------
  function updateCounts(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-hidden', n === 0);
    });
  }

  // --- Abrir / cerrar (mismo contrato que global.js) -----------------------
  function openDrawer() {
    var el = drawer();
    if (!el) return;
    el.setAttribute('data-open', 'true');
    document.documentElement.setAttribute('data-drawer-open', 'true');
  }

  // Cierra el drawer con el mismo contrato de atributos que global.js. Lo
  // usamos para el botón "Seguir comprando" del estado vacío, que además deja
  // continuar la navegación nativa del enlace (a /collections/all).
  function closeDrawer() {
    var el = drawer();
    if (!el) return;
    el.removeAttribute('data-open');
    document.documentElement.removeAttribute('data-drawer-open');
  }

  // --- Upsell: flechas de navegación en escritorio -------------------------
  // La fila de upsell es scrollable (overflow-x:auto) pero con la barra de
  // scroll oculta; en escritorio el ratón no puede arrastrarla, así que
  // revelamos flechas prev/next cuando hay desbordamiento. En móvil las
  // flechas quedan ocultas por CSS (se desliza con el dedo).
  function initUpsell() {
    var el = drawer();
    var track = el && el.querySelector('[data-upsell-track]');
    if (!track) return;
    var prev = el.querySelector('[data-upsell-prev]');
    var next = el.querySelector('[data-upsell-next]');
    if (!prev || !next) return;

    function step() {
      var card = track.querySelector('.glucody-cart-drawer__upsell-card');
      if (!card) return track.clientWidth * 0.8;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 12;
      return card.getBoundingClientRect().width + gap;
    }

    function sync() {
      var overflow = track.scrollWidth - track.clientWidth > 2;
      prev.hidden = !overflow;
      next.hidden = !overflow;
      if (!overflow) return;
      var max = track.scrollWidth - track.clientWidth;
      prev.classList.toggle('is-disabled', track.scrollLeft <= 2);
      next.classList.toggle('is-disabled', track.scrollLeft >= max - 2);
    }

    // Animación propia de scrollLeft (rAF): funciona en TODOS los entornos,
    // incluidos los que dejan el smooth nativo (JS/CSS) en no-op.
    function animateTo(to, dur) {
      var start = track.scrollLeft;
      var max = track.scrollWidth - track.clientWidth;
      var target = Math.max(0, Math.min(max, to));
      var delta = target - start;
      if (Math.abs(delta) < 1) { track.scrollLeft = target; return; }
      var t0 = null, done = false;
      function frame(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        var ease = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; // easeInOutQuad
        track.scrollLeft = start + delta * ease;
        if (p < 1) window.requestAnimationFrame(frame);
        else done = true;
      }
      window.requestAnimationFrame(frame);
      // Fallback: si rAF está pausado (pestaña oculta o motor sin animación),
      // salta al destino para que la flecha SIEMPRE mueva la fila.
      window.setTimeout(function () {
        if (!done && Math.abs(track.scrollLeft - target) > 1) track.scrollLeft = target;
      }, dur + 80);
    }

    prev.addEventListener('click', function () {
      animateTo(track.scrollLeft - step(), 300);
    });
    next.addEventListener('click', function () {
      animateTo(track.scrollLeft + step(), 300);
    });
    track.addEventListener('scroll', sync, { passive: true });

    sync();
  }

  // --- Temporizador de reserva ---------------------------------------------
  function fmt(ms) {
    var total = Math.max(0, Math.ceil(ms / 1000));
    var m = Math.floor(total / 60);
    var s = total % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function initReserve() {
    if (reserveTimer) {
      window.clearInterval(reserveTimer);
      reserveTimer = null;
    }
    var el = drawer();
    var bar = el && el.querySelector('[data-cart-reserve]');

    // Sin barra = carrito vacío → limpia el inicio para que reinicie al volver.
    if (!bar) {
      try { window.localStorage.removeItem(RESERVE_KEY); } catch (e) {}
      return;
    }

    var minutes = parseFloat(bar.getAttribute('data-minutes')) || 10;
    var durationMs = minutes * 60 * 1000;
    var countEl = bar.querySelector('[data-reserve-count]');
    var activeEl = bar.querySelector('[data-reserve-active]');
    var expiredEl = bar.querySelector('[data-reserve-expired]');

    var start;
    try {
      start = parseInt(window.localStorage.getItem(RESERVE_KEY), 10);
    } catch (e) {
      start = NaN;
    }
    if (!start || isNaN(start)) {
      start = Date.now();
      try { window.localStorage.setItem(RESERVE_KEY, String(start)); } catch (e) {}
    }

    var tick = function () {
      var remaining = durationMs - (Date.now() - start);
      if (remaining <= 0) {
        if (activeEl) activeEl.hidden = true;
        if (expiredEl) expiredEl.hidden = false;
        if (reserveTimer) {
          window.clearInterval(reserveTimer);
          reserveTimer = null;
        }
        return;
      }
      if (activeEl) activeEl.hidden = false;
      if (expiredEl) expiredEl.hidden = true;
      if (countEl) countEl.textContent = fmt(remaining);
    };

    tick();
    reserveTimer = window.setInterval(tick, 1000);
  }

  // --- Swap del contenido del drawer (Section Rendering) -------------------

  // --- Multidivisa (QA 15-jul): los umbrales de la barra de metas están en
  // EUR; con otra divisa de presentación (window.Shopify.currency.rate) el
  // Liquid compara céntimos de monedas distintas. Aquí se re-evalúa TODO en
  // cliente: relleno, estado de los hitos, importe restante y sublabels.
  // Con EUR (rate 1) no se toca nada.
  function syncProgressCurrency() {
    var prog = document.querySelector('[data-cart-progress]');
    if (!prog || !window.Shopify || !Shopify.currency) return;
    var rate = parseFloat(Shopify.currency.rate || '1');
    if (!isFinite(rate) || rate <= 0 || rate === 1) return;

    var paid = parseInt(prog.getAttribute('data-paid'), 10) || 0;
    var hasItems = prog.getAttribute('data-has-items') === '1';
    var goalFree = Math.round((parseInt(prog.getAttribute('data-goal-free'), 10) || 0) * rate);
    var giftAttr = prog.getAttribute('data-goal-gift');
    var goalGift = giftAttr ? Math.round(parseInt(giftAttr, 10) * rate) : null;
    var posA = parseFloat(prog.getAttribute('data-pos-a')) || 20;
    var posB = parseFloat(prog.getAttribute('data-pos-b')) || 88;
    if (!goalFree) return;

    function money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'es', {
          style: 'currency',
          currency: Shopify.currency.active
        }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + Shopify.currency.active;
      }
    }

    // relleno segmentado (misma fórmula que el Liquid de la sección)
    var fill;
    if (goalGift !== null) {
      if (paid >= goalGift) fill = 100;
      else if (paid >= goalFree) fill = posB + ((paid - goalFree) * 36 / (goalGift - goalFree));
      else if (hasItems) fill = posA + (paid * 36 / goalFree);
      else fill = 0;
    } else {
      if (paid >= goalFree) fill = 100;
      else if (hasItems) fill = posA + (paid * 68 / goalFree);
      else fill = 0;
    }
    var fillEl = prog.querySelector('.glucody-cart-drawer__bar-fill');
    if (fillEl) fillEl.style.width = Math.max(0, Math.min(100, fill)) + '%';

    // hitos: estado + sublabel con el importe convertido
    var msFree = prog.querySelector('[data-milestone="free"]');
    if (msFree) {
      msFree.classList.toggle('is-reached', paid >= goalFree);
      var subF = msFree.querySelector('[data-milestone-sub]');
      if (subF) subF.textContent = money(goalFree);
    }
    var msGift = prog.querySelector('[data-milestone="gift"]');
    if (msGift && goalGift !== null) {
      msGift.classList.toggle('is-reached', paid >= goalGift);
      var subG = msGift.querySelector('[data-milestone-sub]');
      if (subG) subG.textContent = money(goalGift);
    }

    // texto "te faltan X" ↔ "todo conseguido"
    var next = null;
    if (paid < goalFree) next = goalFree;
    else if (goalGift !== null && paid < goalGift) next = goalGift;
    var rem = prog.querySelector('[data-prog-remaining]');
    var all = prog.querySelector('[data-prog-all]');
    if (rem) rem.hidden = next === null;
    if (all) all.hidden = next !== null;
    var amt = prog.querySelector('[data-remaining-amount]');
    if (amt && next !== null) amt.textContent = money(next - paid);
  }

  // Atributos de ESTADO del raíz que el servidor recalcula en cada render y
  // que innerHTML no refresca (muestra automática + guardia del pack). Sin
  // esta sincronización se quedarían congelados en el primer render.
  var ROOT_STATE_ATTRS = ['data-cart-add-pack', 'data-cart-real', 'data-cart-has-pack', 'data-pack-clamp', 'data-pack-key'];
  function syncRootAttrs(live, next) {
    ROOT_STATE_ATTRS.forEach(function (name) {
      if (next.hasAttribute(name)) live.setAttribute(name, next.getAttribute(name));
      else live.removeAttribute(name);
    });
  }

  function swapFromHTML(html) {
    var live = drawer();
    if (!live) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var next = parsed.querySelector('[data-cart-drawer]');
    if (!next) return;

    // Conserva la posición de scroll del cuerpo (único scroller) entre re-pintados.
    var oldBody = live.querySelector('.cart-drawer__body');
    var scrollTop = oldBody ? oldBody.scrollTop : 0;

    syncRootAttrs(live, next);
    live.innerHTML = next.innerHTML;

    var newBody = live.querySelector('.cart-drawer__body');
    if (newBody) newBody.scrollTop = scrollTop;

    initReserve();
    initUpsell();
    enforceGifts();
    ensurePack();

    syncProgressCurrency();
  }

  // Refresco por GET (para adiciones externas que no devuelven secciones).
  function refresh() {
    var url = window.location.pathname + '?section_id=iurop-cart-drawer';
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) {
        if (!html) return;
        swapFromHTML(html);
        var countEl = drawer() && drawer().querySelector('[data-cart-count]');
        if (countEl) updateCounts(parseInt(countEl.textContent, 10) || 0);
      })
      .catch(function () {});
  }

  // --- Regalo / muestra a elegir -------------------------------------------
  // Añade el producto elegido con la propiedad _gift = tier y refresca el
  // drawer (el 0€ lo aplica el descuento automático del cliente en Admin).
  function addGift(variantId, tier) {
    if (!variantId) return Promise.resolve(false);
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _gift: tier } }] })
    })
      .then(function (res) {
        // Si falla (p.ej. 422: muestra agotada / no comprable) NO disparamos
        // cart:updated → así no se encadena refresh→ensureSample→addGift en
        // bucle infinito martilleando /cart/add.
        if (!res.ok) return false;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { noOpen: true } }));
        return true;
      })
      .catch(function () { return false; });
  }

  // Si el subtotal cae por debajo del hito, el servidor marca el regalo con
  // data-gift-autoremove → lo retiramos (no se regala sin alcanzar el hito).
  function enforceGifts() {
    var el = drawer();
    if (!el) return;
    el.querySelectorAll('[data-gift-autoremove]').forEach(function (flag) {
      var key = flag.getAttribute('data-gift-autoremove');
      if (key) queueChange(key, 0, true);
    });
  }

  // Pack de muestras (spec 17-jul): se auto-añade como LÍNEA NORMAL (sin _gift)
  // cuando hay >=1 producto normal y el pack no está aún (el servidor marca
  // data-cart-add-pack con el variant id). Solo puede haber 1 (auto-add solo
  // si falta + guardia en los flujos de añadir).
  // QA 17-jul: si fue ESTE JS quien añadió el pack (flag en sessionStorage) y
  // el carrito se queda sin productos normales, el pack se auto-quita — el
  // cliente no lo pidió. Si lo añadió el cliente a mano (solo), se queda a 0,01€.
  var PACK_AUTO_FLAG = 'glucodyPackAuto';
  var packInFlight = false;

  function packOrphanCleanup(el) {
    // Sin pack en el carrito → limpiar el flag (el próximo pack puede ser manual)
    if (!el.hasAttribute('data-cart-has-pack')) {
      try { sessionStorage.removeItem(PACK_AUTO_FLAG); } catch (e) {}
      return false;
    }
    var real = parseInt(el.getAttribute('data-cart-real') || '0', 10);
    var key = el.getAttribute('data-pack-key');
    var wasAuto = false;
    try { wasAuto = sessionStorage.getItem(PACK_AUTO_FLAG) === '1'; } catch (e) {}
    if (real === 0 && key && wasAuto) {
      try { sessionStorage.removeItem(PACK_AUTO_FLAG); } catch (e) {}
      queueChange(key, 0, true);
      return true;
    }
    return false;
  }
  function ensurePack() {
    var el = drawer();
    if (!el) return;
    // Defensa "nunca más de 1 pack": si la línea del pack quedó con qty>1, se
    // fija a 1 (el servidor lo marca con data-pack-clamp="<key>").
    var clampKey = el.getAttribute('data-pack-clamp');
    if (clampKey) { queueChange(clampKey, 1, true); return; }
    // Pack auto-añadido que se quedó huérfano (sin productos normales) → quitarlo
    if (packOrphanCleanup(el)) return;
    var addId = el.getAttribute('data-cart-add-pack');
    if (!addId) return;
    // Guarda de reentrada: dos cart:updated casi simultáneos añadirían 2 packs.
    if (packInFlight) return;
    packInFlight = true;
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(addId), quantity: 1 }] })
    })
      .then(function (res) {
        // Si falla (p.ej. pack no comprable) no encadenamos cart:updated para
        // no entrar en bucle refresh→ensurePack→add.
        if (res.ok) {
          // Recordar que lo añadimos NOSOTROS (para auto-quitarlo si queda huérfano)
          try { sessionStorage.setItem(PACK_AUTO_FLAG, '1'); } catch (e) {}
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { noOpen: true } }));
        }
      })
      .catch(function () {})
      .then(function () { packInFlight = false; });
  }

  // --- Cambiar cantidad / eliminar línea (change.js + secciones) -----------
  // Fluidez: la cantidad se actualiza OPTIMISTA en el DOM al instante, los
  // clics rápidos se agrupan (debounce por línea) y solo se envía la cantidad
  // final; las respuestas fuera de orden se descartan (seq) y el re-pintado
  // se salta mientras queden cambios pendientes para no revertir lo que el
  // usuario está viendo. data-busy solo atenúa el subtotal (ver SCSS).
  var pendingQty = {}; // key → cantidad deseada aún sin enviar
  var qtyTimers = {}; // key → debounce
  var reqSeq = 0; // última petición lanzada
  var inFlight = 0; // peticiones vivas

  function hasPending() {
    return Object.keys(pendingQty).length > 0;
  }

  function setBusy(on) {
    var el = drawer();
    if (!el) return;
    if (on) el.setAttribute('data-busy', 'true');
    else el.removeAttribute('data-busy');
  }

  function lineFor(key) {
    var el = drawer();
    var btn = el && el.querySelector('[data-qty-up][data-key="' + key + '"], [data-cart-remove][data-key="' + key + '"]');
    return btn ? btn.closest('.glucody-cart-drawer__item') : null;
  }

  function optimisticQty(key, qty) {
    var line = lineFor(key);
    if (!line) return;
    if (qty <= 0) {
      // La línea desaparece ya; el re-pintado del servidor confirma.
      line.style.opacity = '0.4';
      line.style.pointerEvents = 'none';
      return;
    }
    var value = line.querySelector('.glucody-cart-drawer__qty-value');
    if (value) value.textContent = qty;
    line.querySelectorAll('[data-qty-up], [data-qty-down]').forEach(function (b) {
      b.setAttribute('data-qty', qty);
    });
  }

  function queueChange(key, qty, immediate) {
    pendingQty[key] = qty;
    optimisticQty(key, qty);
    setBusy(true);
    window.clearTimeout(qtyTimers[key]);
    if (immediate) {
      sendChange(key);
    } else {
      qtyTimers[key] = window.setTimeout(function () { sendChange(key); }, 300);
    }
  }

  function sendChange(key) {
    var qty = pendingQty[key];
    delete pendingQty[key];
    delete qtyTimers[key];
    var mySeq = ++reqSeq;
    inFlight++;

    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
    fetch(changeUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: qty,
        sections: 'iurop-cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // Solo pinta la respuesta MÁS RECIENTE y solo si no hay más cambios
        // en cola (el estado del servidor es acumulativo: la última lo trae todo).
        if (mySeq !== reqSeq || hasPending()) return;
        if (cart && cart.sections && cart.sections['iurop-cart-drawer']) {
          swapFromHTML(cart.sections['iurop-cart-drawer']);
        } else {
          return refresh();
        }
        if (cart && typeof cart.item_count === 'number') updateCounts(cart.item_count);
      })
      .catch(function () {
        // Red caída: re-sincroniza con el estado real del servidor.
        if (mySeq === reqSeq && !hasPending()) refresh();
      })
      .then(function () {
        inFlight--;
        if (inFlight <= 0 && !hasPending()) setBusy(false);
      });
  }

  // --- Modal Klarna --------------------------------------------------------
  function klarnaModal() {
    var el = drawer();
    return el && el.querySelector('[data-cart-klarna]');
  }
  function openKlarna() {
    var m = klarnaModal();
    if (m) m.hidden = false;
  }
  function closeKlarna() {
    var m = klarnaModal();
    if (m) m.hidden = true;
  }

  // --- Delegación de eventos ----------------------------------------------
  document.addEventListener('click', function (e) {
    var up = e.target.closest('[data-qty-up]');
    if (up) {
      e.preventDefault();
      var q = (parseInt(up.getAttribute('data-qty'), 10) || 0) + 1;
      queueChange(up.getAttribute('data-key'), q);
      return;
    }

    var down = e.target.closest('[data-qty-down]');
    if (down) {
      e.preventDefault();
      var cur = parseInt(down.getAttribute('data-qty'), 10) || 0;
      queueChange(down.getAttribute('data-key'), Math.max(0, cur - 1));
      return;
    }

    var remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      queueChange(remove.getAttribute('data-key'), 0, true);
      return;
    }

    var giftAdd = e.target.closest('[data-gift-add]');
    if (giftAdd) {
      e.preventDefault();
      if (giftAdd.getAttribute('data-adding')) return; // evita doble-clic
      giftAdd.setAttribute('data-adding', 'true');
      // En éxito el drawer se re-pinta (el botón desaparece); si falla, limpiamos
      // el flag para permitir reintentar.
      addGift(giftAdd.getAttribute('data-variant-id'), giftAdd.getAttribute('data-tier'))
        .then(function (ok) { if (!ok) giftAdd.removeAttribute('data-adding'); });
      return;
    }

    // "Seguir comprando" (estado vacío): cierra el drawer y deja que el enlace
    // navegue de forma nativa a la colección (no hacemos preventDefault).
    if (e.target.closest('[data-cart-continue]')) {
      closeDrawer();
      return;
    }

    if (e.target.closest('[data-cart-klarna-open]')) {
      e.preventDefault();
      openKlarna();
      return;
    }
    if (e.target.closest('[data-cart-klarna-close]')) {
      e.preventDefault();
      closeKlarna();
      return;
    }
  });

  // Esc: cierra primero el modal Klarna (captura → corta el cierre del drawer
  // que hace global.js en fase de burbuja).
  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key !== 'Escape') return;
      var m = klarnaModal();
      if (m && !m.hidden) {
        closeKlarna();
        e.stopPropagation();
      }
    },
    true
  );

  // Adición externa → refresca el contenido. Por defecto abre el drawer; con
  // detail.noOpen (p. ej. el modal quick-view / hero) solo refresca en silencio
  // para no tapar el modal con el sidecart.
  document.addEventListener('cart:updated', function (e) {
    if (e && e.detail && e.detail.noOpen) {
      refresh();
    } else {
      refresh().then(openDrawer);
    }
  });

  // --- Guardia del pack de muestras (spec 17-jul) --------------------------
  // ÚNICA regla: no puede haber 2 packs. Si el pack YA está en el carrito y se
  // intenta añadir otro → se bloquea con el aviso "las muestras ya van
  // incluidas". Todo lo demás está permitido (pack + productos es válido: el
  // pack se vuelve gratis; pack solo es válido a 0,01€). Los flujos de añadir
  // (quick-add, PDP) consultan window.GlucodyCartGuard ANTES del POST.
  function packGuardReason(variantId) {
    var el = drawer();
    if (!el) return null;
    var packs = (el.getAttribute('data-pack-variants') || '').split(',').filter(Boolean);
    if (!packs.length) return null;
    var isPack = packs.indexOf(String(variantId)) !== -1;
    var hasPack = el.getAttribute('data-cart-has-pack') === '1';
    if (isPack && hasPack) return 'pack';
    return null;
  }

  var toastTimer = null;
  function showCartToast(kind) {
    var el = drawer();
    if (!el) return;
    var title = el.getAttribute('data-msg-pack-title') || '';
    var text = el.getAttribute('data-msg-pack-text') || '';
    if (!title && !text) return;

    var toast = document.querySelector('.glucody-cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'glucody-cart-toast';
      toast.setAttribute('role', 'status');
      toast.innerHTML =
        '<span class="glucody-cart-toast__ico" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7s-2.5 0-4-1.5S6.5 2 8 2s4 5 4 5ZM12 7s2.5 0 4-1.5S17.5 2 16 2s-4 5-4 5Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</span>' +
        '<span class="glucody-cart-toast__copy"><strong class="glucody-cart-toast__title"></strong><span class="glucody-cart-toast__text"></span></span>' +
        '<button type="button" class="glucody-cart-toast__close" aria-label="Cerrar">&times;</button>';
      toast.querySelector('.glucody-cart-toast__close').addEventListener('click', function () {
        toast.classList.remove('is-visible');
      });
      document.body.appendChild(toast);
    }
    toast.querySelector('.glucody-cart-toast__title').textContent = title;
    toast.querySelector('.glucody-cart-toast__text').textContent = text;
    // reinicia la animación aunque ya estuviera visible
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, 6000);
  }

  window.GlucodyCartGuard = {
    check: function (variantId) {
      var reason = packGuardReason(variantId);
      if (!reason) return true;
      showCartToast(reason);
      return false;
    }
  };

  // --- Tooltip "?" del hito de muestras -------------------------------------
  // Escritorio: se abre por CSS (:hover). Aquí el toggle por click/tap (móvil
  // y accesibilidad); se cierra al tocar fuera o al re-pintar el drawer.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-milestone-tip-btn]');
    var openTip = document.querySelector('.glucody-cart-drawer__milestone-tip.is-open');
    if (!btn) {
      if (openTip && !e.target.closest('.glucody-cart-drawer__milestone-tip')) {
        openTip.classList.remove('is-open');
        var openBtn = document.querySelector('[data-milestone-tip-btn][aria-expanded="true"]');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      }
      return;
    }
    e.preventDefault();
    var label = btn.closest('.glucody-cart-drawer__milestone-label');
    var tip = label && label.querySelector('.glucody-cart-drawer__milestone-tip');
    if (!tip) return;
    var willOpen = !tip.classList.contains('is-open');
    if (openTip && openTip !== tip) openTip.classList.remove('is-open');
    tip.classList.toggle('is-open', willOpen);
    btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  });

  // Init
  function init() {
    syncProgressCurrency();
    initReserve();
    initUpsell();
    enforceGifts();
    ensurePack();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
